/**
 * Manager for Havok physics integration
 * Provides physics simulation for meshes with collision detection and interactions
 */

import {
  type Mesh,
  PhysicsAggregate,
  PhysicsShapeType,
  Quaternion,
  Ray,
  type Scene,
  Vector3,
} from "@babylonjs/core";
import { HavokPlugin } from "@babylonjs/core/Physics/v2/Plugins/havokPlugin";
import HavokPhysics from "@babylonjs/havok";
import { BaseManager } from "../core/BaseManager.js";
import {
  defaultPhysicsBodyOptions,
  defaultPhysicsOptions,
} from "./physics.schema.js";
import type {
  CollisionCallback,
  PhysicsAggregateInfo,
  PhysicsBodyOptions,
  PhysicsOptions,
  PhysicsRaycastResult,
  Unsubscribe,
} from "./types.js";

/**
 * Map physics shape type string to BabylonJS PhysicsShapeType enum
 */
function getShapeType(shapeType: string): PhysicsShapeType {
  switch (shapeType) {
    case "SPHERE":
      return PhysicsShapeType.SPHERE;
    case "CONVEX_HULL":
      return PhysicsShapeType.CONVEX_HULL;
    default:
      return PhysicsShapeType.BOX;
  }
}

/**
 * Manager responsible for Havok physics integration
 */
export class PhysicsManager extends BaseManager {
  private scene: Scene | null = null;
  private havokInstance: Awaited<ReturnType<typeof HavokPhysics>> | null = null;
  private plugin: HavokPlugin | null = null;
  private physicsAggregates: Map<string, PhysicsAggregateInfo> = new Map();
  private gravity: Vector3 = new Vector3(0, -9.8, 0);
  private collisionCallbacks: Set<CollisionCallback> = new Set();
  private physicsEnabled = false;

  /**
   * Initialize the manager - loads Havok WASM
   */
  protected async onInitialize(): Promise<void> {
    try {
      this.havokInstance = await HavokPhysics();
      this.emit("physics:havok-loaded", { timestamp: Date.now() });
    } catch (error) {
      this.emit("physics:havok-load-error", {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      throw error;
    }
  }

  /**
   * Dispose the manager and cleanup resources
   */
  protected onDispose(): void {
    // Dispose all physics aggregates
    for (const [, info] of this.physicsAggregates) {
      try {
        info.aggregate.dispose();
      } catch {
        // Ignore disposal errors
      }
    }
    this.physicsAggregates.clear();
    this.collisionCallbacks.clear();

    // Dispose plugin
    if (this.plugin) {
      this.plugin.dispose();
      this.plugin = null;
    }

    this.scene = null;
    this.havokInstance = null;
    this.physicsEnabled = false;
  }

  /**
   * Set the scene reference
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }

  /**
   * Get the current scene
   */
  getScene(): Scene | null {
    return this.scene;
  }

  /**
   * Check if physics is enabled
   */
  isPhysicsEnabled(): boolean {
    return this.physicsEnabled;
  }

  /**
   * Enable physics on the scene
   */
  async enablePhysics(options: PhysicsOptions = {}): Promise<void> {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene() first.");
    }

    if (!this.havokInstance) {
      throw new Error("Havok not loaded. Ensure manager is initialized.");
    }

    if (this.physicsEnabled) {
      return; // Already enabled
    }

    const mergedOptions = { ...defaultPhysicsOptions, ...options };

    // Set gravity
    if (mergedOptions.gravity) {
      this.gravity = new Vector3(
        mergedOptions.gravity.x,
        mergedOptions.gravity.y,
        mergedOptions.gravity.z,
      );
    }

    // Create Havok plugin
    this.plugin = new HavokPlugin(true, this.havokInstance);

    // Enable physics on scene
    this.scene.enablePhysics(this.gravity, this.plugin);
    this.physicsEnabled = true;

    this.emit("physics:enabled", {
      gravity: { x: this.gravity.x, y: this.gravity.y, z: this.gravity.z },
    });
  }

  /**
   * Disable physics on the scene
   */
  disablePhysics(): void {
    if (!this.physicsEnabled || !this.scene) {
      return;
    }

    // Dispose all physics bodies
    for (const [, info] of this.physicsAggregates) {
      try {
        info.aggregate.dispose();
      } catch {
        // Ignore disposal errors
      }
    }
    this.physicsAggregates.clear();

    // Disable physics on scene
    this.scene.disablePhysicsEngine();
    this.physicsEnabled = false;

    if (this.plugin) {
      this.plugin.dispose();
      this.plugin = null;
    }

    this.emit("physics:disabled", {});
  }

  /**
   * Add a physics body to a mesh
   */
  addPhysicsBody(
    mesh: Mesh,
    options: PhysicsBodyOptions,
  ): PhysicsAggregate | null {
    this.ensureInitialized();

    if (!this.scene || !this.physicsEnabled) {
      throw new Error("Physics not enabled. Call enablePhysics() first.");
    }

    const meshId = mesh.id || mesh.name;

    // Remove existing body if present
    if (this.physicsAggregates.has(meshId)) {
      this.removePhysicsBody(meshId);
    }

    const mergedOptions = { ...defaultPhysicsBodyOptions, ...options };

    // Build aggregate options
    const aggregateOptions: {
      friction?: number;
      mass: number;
      restitution?: number;
      extents?: Vector3;
      radius?: number;
      center?: Vector3;
    } = {
      friction: mergedOptions.friction,
      mass: mergedOptions.mass,
      restitution: mergedOptions.restitution,
    };

    // Apply custom extents for BOX shapes (thick floor pattern)
    if (mergedOptions.shapeType === "BOX" && mergedOptions.extents) {
      aggregateOptions.extents = new Vector3(
        mergedOptions.extents.x,
        mergedOptions.extents.y,
        mergedOptions.extents.z,
      );
    }

    // Apply custom radius for SPHERE shapes (avoids non-uniform scaling warning)
    if (mergedOptions.shapeType === "SPHERE" && mergedOptions.radius) {
      aggregateOptions.radius = mergedOptions.radius;
    }

    // Apply custom center offset (e.g., shift floor down so top surface is at Y=0)
    if (mergedOptions.center) {
      aggregateOptions.center = new Vector3(
        mergedOptions.center.x,
        mergedOptions.center.y,
        mergedOptions.center.z,
      );
    }

    // Create physics aggregate
    const aggregate = new PhysicsAggregate(
      mesh,
      getShapeType(mergedOptions.shapeType),
      aggregateOptions,
      this.scene,
    );

    // Apply damping if specified
    if (
      mergedOptions.linearDamping !== undefined ||
      mergedOptions.angularDamping !== undefined
    ) {
      aggregate.body.setLinearDamping(mergedOptions.linearDamping ?? 0);
      aggregate.body.setAngularDamping(mergedOptions.angularDamping ?? 0);
    }

    // Store reference
    this.physicsAggregates.set(meshId, {
      aggregate,
      mesh,
      meshId,
    });

    this.emit("physics:body-added", { meshId });

    return aggregate;
  }

  /**
   * Remove a physics body from a mesh
   */
  removePhysicsBody(meshId: string): void {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      try {
        info.aggregate.dispose();
      } catch {
        // Ignore disposal errors
      }
      this.physicsAggregates.delete(meshId);
      this.emit("physics:body-removed", { meshId });
    }
  }

  /**
   * Get the physics aggregate for a mesh
   */
  getPhysicsBody(meshId: string): PhysicsAggregate | null {
    return this.physicsAggregates.get(meshId)?.aggregate ?? null;
  }

  /**
   * Get all physics aggregates
   */
  getAllPhysicsBodies(): Map<string, PhysicsAggregateInfo> {
    return new Map(this.physicsAggregates);
  }

  /**
   * Set the gravity vector
   */
  setGravity(gravity: Vector3): void {
    this.gravity = gravity;

    if (this.plugin && this.physicsEnabled) {
      this.plugin.setGravity(gravity);
      this.emit("physics:gravity-changed", {
        gravity: { x: gravity.x, y: gravity.y, z: gravity.z },
      });
    }
  }

  /**
   * Get the current gravity vector
   */
  getGravity(): Vector3 {
    return this.gravity.clone();
  }

  /**
   * Perform a raycast
   */
  raycast(from: Vector3, to: Vector3): PhysicsRaycastResult {
    const result: PhysicsRaycastResult = {
      body: null,
      hasHit: false,
      hitDistance: 0,
      hitNormal: null,
      hitPoint: null,
      mesh: null,
    };

    if (!this.scene || !this.physicsEnabled) {
      return result;
    }

    // Use scene picking as a fallback since Havok raycast requires more setup
    const direction = to.subtract(from);
    const length = direction.length();
    direction.normalize();

    const ray = new Ray(from, direction, length);
    const pickInfo = this.scene.pickWithRay(ray, (mesh) => {
      const meshId = mesh.id || mesh.name;
      return this.physicsAggregates.has(meshId);
    });

    if (pickInfo?.hit && pickInfo.pickedMesh) {
      result.hasHit = true;
      result.hitPoint = pickInfo.pickedPoint ?? null;
      result.hitNormal = pickInfo.getNormal() ?? null;
      result.hitDistance = pickInfo.distance;
      result.mesh = pickInfo.pickedMesh as Mesh;

      const meshId = pickInfo.pickedMesh.id || pickInfo.pickedMesh.name;
      const info = this.physicsAggregates.get(meshId);
      if (info) {
        result.body = info.aggregate.body;
      }
    }

    return result;
  }

  /**
   * Apply an impulse to a physics body
   */
  applyImpulse(meshId: string, impulse: Vector3, contactPoint?: Vector3): void {
    const info = this.physicsAggregates.get(meshId);
    if (!info) {
      return;
    }

    const point = contactPoint ?? info.mesh.getAbsolutePosition();
    info.aggregate.body.applyImpulse(impulse, point);
  }

  /**
   * Apply a radial impulse to all bodies within radius
   */
  applyRadialImpulse(origin: Vector3, radius: number, strength: number): void {
    for (const [, info] of this.physicsAggregates) {
      // Skip static bodies (mass === 0)
      const mass = info.aggregate.body.getMassProperties().mass;
      if (mass === 0) {
        continue;
      }

      const meshPosition = info.mesh.getAbsolutePosition();
      const direction = meshPosition.subtract(origin);
      const distance = direction.length();

      if (distance < radius && distance > 0.01) {
        // Falloff based on distance
        const falloff = 1 - distance / radius;
        const impulseStrength = strength * falloff;

        const impulse = direction.normalize().scale(impulseStrength);
        info.aggregate.body.applyImpulse(impulse, meshPosition);
      }
    }

    this.emit("physics:radial-impulse", {
      origin: { x: origin.x, y: origin.y, z: origin.z },
      radius,
      strength,
    });
  }

  /**
   * Wake all sleeping physics bodies
   */
  wakeAll(): void {
    for (const [, info] of this.physicsAggregates) {
      info.aggregate.body.setMotionType(1); // PhysicsMotionType.DYNAMIC
    }
  }

  /**
   * Subscribe to collision events
   */
  onCollision(callback: CollisionCallback): Unsubscribe {
    this.collisionCallbacks.add(callback);
    return () => {
      this.collisionCallbacks.delete(callback);
    };
  }

  /**
   * Set linear velocity of a physics body
   */
  setLinearVelocity(meshId: string, velocity: Vector3): void {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      info.aggregate.body.setLinearVelocity(velocity);
    }
  }

  /**
   * Set angular velocity of a physics body
   */
  setAngularVelocity(meshId: string, velocity: Vector3): void {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      info.aggregate.body.setAngularVelocity(velocity);
    }
  }

  /**
   * Get linear velocity of a physics body
   */
  getLinearVelocity(meshId: string): Vector3 | null {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      return info.aggregate.body.getLinearVelocity();
    }
    return null;
  }

  /**
   * Get angular velocity of a physics body
   * Used for rest detection in gradual rotation correction
   */
  getAngularVelocity(meshId: string): Vector3 | null {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      return info.aggregate.body.getAngularVelocity();
    }
    return null;
  }

  /**
   * Get current angular damping of a physics body
   */
  getAngularDamping(meshId: string): number | null {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      return info.aggregate.body.getAngularDamping();
    }
    return null;
  }

  /**
   * Set angular damping of a physics body
   * Used for dynamic damping based on grab point (edge grabs = less damping)
   */
  setAngularDamping(meshId: string, damping: number): void {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      info.aggregate.body.setAngularDamping(damping);
    }
  }

  /**
   * Get current linear damping of a physics body
   */
  getLinearDamping(meshId: string): number | null {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      return info.aggregate.body.getLinearDamping();
    }
    return null;
  }

  /**
   * Set linear damping of a physics body
   */
  setLinearDamping(meshId: string, damping: number): void {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      info.aggregate.body.setLinearDamping(damping);
    }
  }

  /**
   * Disable pre-step for a body (useful during drag)
   */
  setDisablePreStep(meshId: string, disable: boolean): void {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      info.aggregate.body.disablePreStep = disable;
    }
  }

  /**
   * Set target transform for a physics body (used during drag)
   * The body will smoothly move toward this position/rotation
   */
  setTargetTransform(
    meshId: string,
    position: Vector3,
    rotation?: Quaternion,
  ): void {
    const info = this.physicsAggregates.get(meshId);
    if (info) {
      info.aggregate.body.setTargetTransform(
        position,
        rotation ??
          info.aggregate.transformNode.rotationQuaternion ??
          Quaternion.Identity(),
      );
    }
  }

  /**
   * Set motion type for physics body
   * ANIMATED = kinematic (we control position, physics follows)
   * DYNAMIC = physics simulation active (gravity, collisions)
   */
  setMotionType(meshId: string, type: "ANIMATED" | "DYNAMIC"): void {
    const info = this.physicsAggregates.get(meshId);
    if (!info) return;

    // PhysicsMotionType: 0 = STATIC, 1 = DYNAMIC, 2 = ANIMATED
    const motionType = type === "ANIMATED" ? 2 : 1;
    info.aggregate.body.setMotionType(motionType);
  }

  /**
   * Toggle "Ghost Mode" - disable collisions with other dynamic objects while dragging
   * Prevents bulldozing other runes but maintains floor/static collision
   *
   * Collision groups:
   * - Bit 0 (0x1): Static geometry (floor, walls) - always collide
   * - Bit 1 (0x2): Dynamic objects (runes) - disabled in ghost mode
   */
  setGhostMode(meshId: string, enabled: boolean): void {
    const info = this.physicsAggregates.get(meshId);
    if (!info) return;

    const shape = info.aggregate.shape;
    if (enabled) {
      // Ghost: collide only with static geometry (bit 0), not other dynamics
      // Membership stays full so other non-ghost objects can still detect us
      shape.filterMembershipMask = 0xffffffff;
      shape.filterCollideMask = 0x1; // Only collide with static (floor)
    } else {
      // Normal: collide with everything
      shape.filterMembershipMask = 0xffffffff;
      shape.filterCollideMask = 0xffffffff;
    }
  }
}
